import { compareNuGetVersions } from './required-packages.js';

/**
 * A single NU1605 (package downgrade) conflict, reduced to the action that resolves it:
 * the DIRECT PackageReference of `package` in `project` must be bumped up to
 * `requiredVersion` so it no longer downgrades a transitive constraint.
 */
export interface NuGetDowngrade {
  /** Project (assembly) name exactly as NuGet prints it, e.g. `PickEBike.Infrastructure`. */
  project: string;
  /** Package id, e.g. `Microsoft.EntityFrameworkCore`. */
  package: string;
  /** Highest `(>= x)` floor seen for the pair — the version to pin the direct reference to. */
  requiredVersion: string;
}

/**
 * Parses `dotnet restore` output for NU1605 (package downgrade) conflicts and returns,
 * per (project, package), the version the DIRECT reference must be bumped to so it stops
 * downgrading a transitive constraint introduced by the upgrade.
 *
 * This is the recurring failure when SmartStack raises its transitive floor for a
 * Microsoft.* package (e.g. EF Core 10.0.5 → 10.0.7) while the client `.csproj` still
 * pins the old version directly.
 *
 * Locale-independent BY DESIGN: it reads ONLY the dependency-path lines NuGet prints
 * under each NU1605, which are never translated:
 *     PickEBike.Infrastructure -> SmartStack 3.56.1 -> Microsoft.EntityFrameworkCore (>= 10.0.7)
 *     PickEBike.Infrastructure -> Microsoft.EntityFrameworkCore (>= 10.0.5)
 * The localized headline ("package downgrade detected" / "Passage à une version
 * antérieure …") is ignored. For each pair the required version is the MAX `(>= …)`
 * floor across all its paths — the transitive constraint the direct reference must meet.
 *
 * Returns `[]` when the output carries no NU1605 (a clean restore, or a different error
 * we cannot auto-repair).
 */
export function parseNuGetDowngrades(restoreOutput: string): NuGetDowngrade[] {
  // Gate on the code so we never harvest dependency-graph lines from unrelated diagnostics.
  if (!restoreOutput.includes('NU1605')) return [];

  // key: `${project}\0${package.toLowerCase()}` → highest floor seen for that pair.
  const floors = new Map<string, NuGetDowngrade>();

  for (const rawLine of restoreOutput.split(/\r?\n/)) {
    if (!rawLine.includes('->') || !rawLine.includes('(>=')) continue;

    const segments = rawLine.split('->').map((s) => s.trim()).filter(Boolean);
    if (segments.length < 2) continue;

    const project = segments[0];
    const tail = segments[segments.length - 1]; // e.g. "Microsoft.EntityFrameworkCore (>= 10.0.7)"
    const m = tail.match(/^([A-Za-z0-9_.\-]+)\s*\(>=\s*([0-9][0-9A-Za-z.\-]*)\)$/);
    if (!m) continue;

    const pkg = m[1];
    const version = m[2];
    const key = `${project} ${pkg.toLowerCase()}`;
    const prev = floors.get(key);
    if (!prev || compareNuGetVersions(version, prev.requiredVersion) > 0) {
      floors.set(key, { project, package: pkg, requiredVersion: version });
    }
  }

  return [...floors.values()];
}
